import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot } from '@angular/router';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { NotificationService } from 'src/app/notification.service';
import { environment } from 'src/environments/environment';
import { ModulesService } from 'src/app/services/Authetication/menu-permission/modules.service';
import { LoggingService } from 'src/app/services/logging.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { DatatransferService } from 'src/app/services/DataTransfer/datatransfer.service';
import * as commonConstant from 'src/app/services/common.constant';
import { clearLinkoutFlowStateStorage } from 'src/app/services/linkout/linkout.service';
import { LINKOUT_FEATURE_ENABLED } from 'src/app/services/linkout/linkout-feature.config';

@Injectable({
  providedIn: 'root'
})
export class LoginAuthenticationGuardService {
  loCurrentUserData: any;
  assignedModules = false;
  constructor(private _router: Router,
   private httpClient: HttpClient,
   private _notificationService: NotificationService,
	 private _modulesService: ModulesService,
	 private _dataService: DatatransferService,
	 private spinner: NgxSpinnerService,
	 private logger: LoggingService
	 ) {
		this.loCurrentUserData = [];
	  }
	canActivate(next: ActivatedRouteSnapshot,
		state: RouterStateSnapshot): Observable<boolean> | boolean {

		const parsedUrl = this._router.parseUrl(state.url);
		const qp = parsedUrl.queryParams;

		const pathNoQueryEarly = state.url.split('?')[0];
		if (
			!LINKOUT_FEATURE_ENABLED &&
			(pathNoQueryEarly === '/linkout' || pathNoQueryEarly.startsWith('/linkout/'))
		) {
			void this._router.navigate(['/requests'], { replaceUrl: true });
			return false;
		}

		const loginData =
			next.queryParams['loginData'] ||
			qp['loginData'];

		// Link-out SSO return — only when loginData is real user JSON (not placeholders like %22%22 / "").
		// Otherwise we must fall through so correlationId/claimNumber can trigger api/linkout/Redirect.
		if (LINKOUT_FEATURE_ENABLED && state.url.includes('/linkout') && loginData) {
			try {
				const decoded = JSON.parse(decodeURIComponent(loginData));
				if (decoded && typeof decoded === 'object' && !Array.isArray(decoded)) {
					this.persistSessionFromLoginData(loginData);
					return true;
				}
			} catch {
				/* fall through to link-out entry */
			}
		}

		const identifier =
			next.queryParams['identifier'] ||
			next.queryParams['iss'] ||
			qp['identifier'] ||
			qp['iss'];

		const subscriptionIdParam =
			next.queryParams['subscriptionId'] ||
			qp['subscriptionId'];

		// ClaimSearch partner link (identifier): always re-run SSO — before loginData or stored Verisk token.
		if (identifier) {
			try {
				localStorage.removeItem('veriskToken');
				localStorage.removeItem('veriskUserData');
				localStorage.removeItem('currentUserData');
			} catch (_) {}
			const fullReturnUrl = window.location.origin + (state.url.startsWith('/') ? state.url : '/' + state.url);
			let apiUrl = `${environment.WebApiURL}api/Entry/resolve?identifier=${encodeURIComponent(identifier)}&returnUrl=${encodeURIComponent(fullReturnUrl)}`;
			if (subscriptionIdParam) {
				apiUrl += `&subscriptionId=${encodeURIComponent(subscriptionIdParam)}`;
			}
			try {
				localStorage.setItem('veriskEntryResolveUrl', apiUrl);
			} catch (_) {}
			this.logger.logIdentifierDetected(state.url, identifier, fullReturnUrl, 'LoginAuthenticationGuardService');
			this.logger.logSsoLoginRedirect(state.url, identifier, fullReturnUrl, apiUrl);
			window.location.href = apiUrl;
			return false;
		}

		// Post-SSO callback on /requests (loginData only, no identifier).
		if (state.url.includes('/requests') && loginData) {
			this.persistSessionFromLoginData(loginData);
			return true;
		}

		const linkoutErrorMessage =
			next.queryParams['linkoutErrorMessage'] ||
			qp['linkoutErrorMessage'] ||
			next.queryParams['errorMessage'] ||
			qp['errorMessage'];
		const linkoutErrorCode =
			next.queryParams['linkoutErrorCode'] ||
			qp['linkoutErrorCode'] ||
			next.queryParams['errorCode'] ||
			qp['errorCode'];
		if (LINKOUT_FEATURE_ENABLED && state.url.includes('/requests') && (linkoutErrorMessage || linkoutErrorCode)) {
			return true;
		}

		// Link-out deep link: full-page redirect to API with returnUrl (same pattern as identifier → api/Entry/resolve)
		const pathNoQuery = state.url.split('?')[0];
		const isLinkoutPath = pathNoQuery === '/linkout' || pathNoQuery.startsWith('/linkout/');
		const correlationId = qp['correlationId'] || next.queryParams['correlationId'];
		const claimNumber = qp['claimNumber'] || next.queryParams['claimNumber'];
		if (LINKOUT_FEATURE_ENABLED && isLinkoutPath) {
			if (!correlationId || !claimNumber) {
				return true;
			}
			// Link-out partner entry: always re-validate via api/linkout/Redirect (do not reuse stale localStorage session).
			try {
				localStorage.removeItem('veriskToken');
				localStorage.removeItem('veriskUserData');
				localStorage.removeItem('currentUserData');
			} catch (_) {}
			// Full-page entry to API (same pattern as /requests?identifier).
			// returnUrl targets /requests with link-out params so backend can redirect to:
			// /requests?loginData=…&correlationId=…&claimNumber=… then the list page strips loginData and opens Add Request.
			const fullReturnUrl =
				`${window.location.origin}/requests?correlationId=${encodeURIComponent(correlationId)}&claimNumber=${encodeURIComponent(claimNumber)}`;
			let apiUrl = `${environment.WebApiURL}api/linkout/Redirect?correlationId=${encodeURIComponent(correlationId)}&claimNumber=${encodeURIComponent(claimNumber)}&returnUrl=${encodeURIComponent(fullReturnUrl)}`;
			try {
				localStorage.setItem('linkoutEntryResolveUrl', apiUrl);
			} catch (_) {}
			this.logger.logIdentifierDetected(state.url, `linkout:${correlationId}`, fullReturnUrl, 'LoginAuthenticationGuardService');
			window.location.href = apiUrl;
			return false;
		}

		// request-details with Error param: allow through so details page can show unauthorized message instead of redirecting to login
		const errorParam = next.queryParams['Error'] || this._router.parseUrl(state.url).queryParams['Error'];
		if (state.url.includes('request-details') && errorParam) {
			return true;
		}

		console.log('[GUARD DEBUG] Route guard activated for:', state.url);
		console.log('[GUARD DEBUG] Checking authentication...');
		
		// Per Verisk spec: tokens should NEVER be in URL query parameters
		// Only check localStorage/cookies for session
		this.loCurrentUserData = JSON.parse(localStorage.getItem('currentUserData') || '{}');
		console.log('[GUARD DEBUG] Current user data in localStorage:', {
			hasData: !!localStorage.getItem('currentUserData'),
			hasAccessToken: typeof this.loCurrentUserData.accessToken !== 'undefined',
			userId: this.loCurrentUserData.userId,
			roleId: this.loCurrentUserData.roleId
		});
		
		// Check for Verisk token in localStorage or cookies (for /requests route)
		const veriskTokenStored = localStorage.getItem('veriskToken') || this.getCookie('veriskToken') || this.getCookie('verisk_access_token');
		const isRequestsRoute = state.url.includes('/requests');
		console.log('[GUARD DEBUG] isRequestsRoute:', isRequestsRoute);

		// If accessing /requests route and Verisk token exists (no identifier in URL), allow through to VeriskAuthGuard
		if (isRequestsRoute && veriskTokenStored) {
			console.log('[GUARD DEBUG] Verisk session detected for /requests route - allowing VeriskAuthGuard to handle');
			return true;
		}
		
		if (typeof this.loCurrentUserData.accessToken == 'undefined' && (this.loCurrentUserData == null || typeof this.loCurrentUserData.accessToken == 'undefined')) {
			// Check for Verisk deep link: identifier (iss or identifier) in URL - call Verisk SSO API
			const identifierParam = next.queryParams['iss'] || next.queryParams['identifier'] ||
				this._router.parseUrl(state.url).queryParams['iss'] || this._router.parseUrl(state.url).queryParams['identifier'];
			const subscriptionIdParam = next.queryParams['subscriptionId'] ||
				this._router.parseUrl(state.url).queryParams['subscriptionId'];
			if (isRequestsRoute && identifierParam) {
				const fullReturnUrl = window.location.origin + (state.url.startsWith('/') ? state.url : '/' + state.url);
				let apiUrl = `${environment.WebApiURL}api/Entry/resolve?identifier=${encodeURIComponent(identifierParam)}&returnUrl=${encodeURIComponent(fullReturnUrl)}`;
				if (subscriptionIdParam) {
					apiUrl += `&subscriptionId=${encodeURIComponent(subscriptionIdParam)}`;
				}
				try {
					localStorage.setItem('veriskEntryResolveUrl', apiUrl);
				} catch (_) {}
				this.logger.logIdentifierDetected(state.url, identifierParam, fullReturnUrl, 'LoginAuthenticationGuardService');
				this.logger.logSsoLoginRedirect(state.url, identifierParam, fullReturnUrl, apiUrl);
				window.location.href = apiUrl;
				return false;
			}
			this.logger.logGuardCheck(state.url, !!veriskTokenStored, false, 'LoginAuthenticationGuardService');
			this.logger.logNoSessionRedirect(state.url, false);
			this._router.navigate(['/login']);
			return false;
		}
		
		console.log('[GUARD DEBUG] ✅ User authenticated, proceeding with route activation');
		
		// Check if we have token but no roleId (SSO flow - token stored but user info not yet fetched)
		if (this.loCurrentUserData.accessToken && !this.loCurrentUserData.roleId) {
			console.log('[GUARD DEBUG] ⚠️ Token exists but roleId missing - allowing route to proceed');
			console.log('[GUARD DEBUG] Component will handle fetching user info if needed');
			// Allow route to proceed - components can fetch user info using the token
			return true;
		}
		
		// If no roleId, we can't make the modules API call
		if (!this.loCurrentUserData.roleId) {
			console.log('[GUARD DEBUG] ⚠️ No roleId available - allowing route to proceed');
			return true;
		}
		
		const httpOptions = {
			headers: {
				'Content-Type': 'application/json',
				'Authorization': 'Bearer ' + this.getAccessTokenFromLocal() //this.loCurrentUserData.accessToken
			},
			params: { 'uiRoleId': this.loCurrentUserData.roleId }
		};

	// Capture navigation state BEFORE the async HTTP call so the value is available in the closure.
	// When secure-pdf.component navigates here after a permission denial it sets fromPdfDenied=true
	// so the guard skips showing its own "no permission" toast (secure-pdf already showed one).
	const fromPdfDenied = !!(this._router.getCurrentNavigation()?.extras?.state?.['fromPdfDenied']);

	return this.httpClient.get<any>(environment.WebApiURL + 'api/Authentication/GetModulesByRoleId', httpOptions).pipe(
		map((res) => {
			if (res['Error']) {
				this._router.navigate(['/login'], { queryParams: { returnUrl: state.url } });
				return false;
			}
			else {
				this._modulesService.modulesByRole = res.data.modules;
				var isPresent = res.data.modules.some((el: any) => el.stRouterLink === state.url.split('?')[0].slice(1).split('/')[0])
					if (this.loCurrentUserData !== null && (this.loCurrentUserData != null || this.loCurrentUserData != '')) {
						if (this.loCurrentUserData.isFlgIsClientExpired && !this.loCurrentUserData.isSuperAdmin) {
							this._router.navigate(["client/expiration"]);
						}
						else if (state.url.indexOf("history") !== -1 && this.loCurrentUserData.IsHistoryAccess == false) {
							this.CheckRoleWiseMenu(res.data.modules);
							this._notificationService.showInfo("You don't have permission to access this page", "Permission");
							return false;
						}
						else if (state.url.indexOf("profile-setting") !== -1) {
							return true;
						}
						else if (state.url.indexOf("viewclient") !== -1) {
							var hasClientsAccess = res.data.modules.some((el: any) => el.stRouterLink === 'clients');
							if (hasClientsAccess || this.loCurrentUserData.isSuperAdmin) {
								return true;
							}
							this.CheckRoleWiseMenu(res.data.modules);
							this._notificationService.showInfo("You don't have permission to access this page", "Permission");
							return false;
						}
						else if (state.url.indexOf("workbench-details") !== -1) {
							var hasWorkbenchAccess = res.data.modules.some((el: any) => el.stRouterLink === 'workbench');
							if (hasWorkbenchAccess) {
								return true;
							}
							this.CheckRoleWiseMenu(res.data.modules);
							this._notificationService.showInfo("You don't have permission to access this page", "Permission");
							return false;
						}
						else if (this.loCurrentUserData !== null && (this.loCurrentUserData != null || this.loCurrentUserData != '') && isPresent) {
							return true;
						}
						else {
							this.CheckRoleWiseMenu(res.data.modules);
							if (!isPresent && !fromPdfDenied) {
								this._notificationService.showInfo("You don't have permission to access this page", "Permission");
							}
							return false;
						}
					}
					else {
						this._notificationService.showInfo("You don't have access this menus link", "Permission");
						this._router.navigate(['/login'], { queryParams: { returnUrl: state.url } });
					}
				}
				return true;
			}),
			catchError((err) => {
				console.log('canActivate method given error : ' + err.status);

				if (err != undefined) {
					if (err.status == 401 && err.statusText == "Unauthorized") {
						if (this._router.url == "/" || this._router.url == "/login") {
							this._router.navigate(['/login']);
							setTimeout(function () {
								localStorage.removeItem("IsLogged");
								localStorage.removeItem('currentUserData');
								localStorage.removeItem('ViewClienName');
								localStorage.removeItem('LoggedInRole');
								localStorage.removeItem('userAddEditAction');
								localStorage.removeItem('teamAddEditAction');
								localStorage.removeItem('SuperAdminUserName');
								localStorage.removeItem('SuperAdminUserId');
								localStorage.removeItem('SuperAdminUserRole');
								localStorage.removeItem('currentViewClientExpiryDate');
							}, 2000);
						} else {
							this._notificationService.showInfo(commonConstant.expiredSessionErrorMessage, "Information");
						}
					}
				}
				return of(false);
			})
		);
	}


  getAccessTokenFromLocal():any {
    let LocalToken = JSON.parse(localStorage.getItem('currentUserData') || '');

		if (LocalToken == undefined || LocalToken == "" || LocalToken == null) {
			return this.getAccessTokenFromLocal()
		}
		else {
			return LocalToken.accessToken;
		}
  }
  
    async CheckRoleWiseMenu(modules?: any[]) {
		if (modules && modules.length > 0) {
			const firstModule = modules[0];
			if (firstModule && firstModule.stRouterLink) {
				this._router.navigate(['/' + firstModule.stRouterLink]);
				return;
			}
		}
		await this.getRoleByRoleId()
	}

	getRoleByRoleId() {
		this.spinner.show();
		this._modulesService.getRoleByRoleId(this.loCurrentUserData.roleId).subscribe(
			(response: any) => {
				if (!!response) {
					if (response.statusCode == 200) {
						const userModules = this._modulesService.modulesByRole;
						
						if (userModules && userModules.length > 0) {
							const firstModule = userModules[0];
							if (firstModule && firstModule.stRouterLink) {
								this._router.navigate(['/' + firstModule.stRouterLink]);
								this.spinner.hide();
								return;
							}
						}
						
						if (response.data.role.name.toLowerCase() == "super admin" || response.data.role.name.toLowerCase() == "client admin") {
							this._router.navigate(['/utilization']);
						}
						else if (response.data.role.name.toLowerCase() == "report reviewer" 
						|| response.data.role.name.toLowerCase() == "approver"
						|| response.data.role.name.toLowerCase() == "publisher") {
							this._router.navigate(['/workbench']);
						}
						else {
							this._router.navigate(['/requests']);
						}
					}
				}
			}, (error) => {

			});
		this.spinner.hide();
	}

	logout() {
		this._dataService.teamMessage.subscribe(message => {
			message.message = '';
		});

		localStorage.removeItem("IsLogged");
		localStorage.removeItem('currentUserData');
		localStorage.removeItem('ViewClienName');
		localStorage.removeItem('LoggedInRole');
		localStorage.removeItem('userAddEditAction');
		localStorage.removeItem('teamAddEditAction');
		localStorage.removeItem('SuperAdminUserName');
		localStorage.removeItem('SuperAdminUserId');
		localStorage.removeItem('SuperAdminUserRole');
		// Clear Verisk-specific tokens
		localStorage.removeItem('veriskToken');
		localStorage.removeItem('veriskUserData');
		if (LINKOUT_FEATURE_ENABLED) {
			clearLinkoutFlowStateStorage();
		}
		this._router.navigate(['login']);
	}

	private persistSessionFromLoginData(loginData: string): void {
		try {
			const decoded = JSON.parse(decodeURIComponent(loginData));
			if (decoded && typeof decoded === 'object') {
				localStorage.removeItem('SuperAdminUserName');
				localStorage.removeItem('SuperAdminUserId');
				localStorage.removeItem('SuperAdminUserRole');
				localStorage.removeItem('flgIsLoggedInAsUser');
				const userData = { ...decoded, isVeriskUser: true };
				localStorage.setItem('currentUserData', JSON.stringify(userData));
				if (decoded.accessToken) {
					localStorage.setItem('veriskToken', decoded.accessToken);
					localStorage.setItem('veriskUserData', JSON.stringify(decoded));
				}
			}
		} catch (_) {
			// ignore
		}
	}

	/**
	 * Gets a cookie value by name
	 * @param name - Cookie name
	 * @returns Cookie value or null if not found
	 */
	private getCookie(name: string): string | null {
		const value = `; ${document.cookie}`;
		const parts = value.split(`; ${name}=`);
		if (parts.length === 2) {
			return parts.pop()?.split(';').shift() || null;
		}
		return null;
	}
}